import { FC } from 'react';
import PageLayout from '@/components/ui/PageLayout/PageLayout.tsx';
import { useTranslation } from 'react-i18next';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';
import { Alert, Button } from 'react-daisyui';
import { useAuthContext } from '@/context/AuthContext.tsx';
import dayjs from 'dayjs';
import useSubscriptionCancel from '@/api/stripe/useSubscriptionCancel.ts';
import { passToRouteSearchParams } from '@/utils/passToRouteSearchParams.ts';
import SearchParams from '@/_constants/SearchParams.ts';
import useSubscriptionReactivate from '@/api/stripe/useSubscriptionReactivate.ts';

type SubscriptionPageProps = {};

const SubscriptionPage: FC<SubscriptionPageProps> = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();

  const subscription = user?.active_subscription;
  const isSubscriptionCancelled = !!subscription?.cancel_at_period_end;

  const {
    mutateAsync: cancelSubscription,
    isPending: isLoadingCancelSubscription,
    error: errorCancelSubscription,
    isSuccess: isSuccessCancelSubscription,
  } = useSubscriptionCancel();

  const {
    mutateAsync: reactivateSubscription,
    isPending: isLoadingReactivateSubscription,
    error: errorReactivateSubscription,
    isSuccess: isSuccessReactivateSubscription,
  } = useSubscriptionReactivate();

  const handleGoToPaymentPage = () => {
    const redirectUrl = new URL(window.location.href);

    window.location.href = passToRouteSearchParams(`${import.meta.env.VITE_FRONTEND_URL}/payment-select`, {
      [SearchParams.REDIRECT_URL]: encodeURIComponent(redirectUrl.toString()),
    });
  };

  const handleCancelSubscription = () => {
    void (async () => {
      try {
        if (isLoadingCancelSubscription || !subscription?.subscription_id) return;

        await cancelSubscription({
          subscription_id: subscription.subscription_id,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  const handleReactivateSubscription = () => {
    void (async () => {
      try {
        if (isLoadingReactivateSubscription || !subscription?.subscription_id) return;

        await reactivateSubscription({
          subscription_id: subscription.subscription_id,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <PageLayout name={t('subscriptionPage.title')}>
      {isSuccessCancelSubscription ? (
        <Alert status="success">{t('subscriptionPage.subscriptionCancelled')}</Alert>
      ) : null}
      {isSuccessReactivateSubscription ? (
        <Alert status="success">{t('subscriptionPage.subscriptionRestored')}</Alert>
      ) : null}
      {errorCancelSubscription ? <Alert status="error">{errorCancelSubscription.message}</Alert> : null}
      {errorReactivateSubscription ? <Alert status="error">{errorReactivateSubscription.message}</Alert> : null}
      <div className="flex flex-col gap-4">
        <p>{t('subscriptionPage.header')}:</p>
        <p>
          {t('subscriptionPage.currentSubscription')}:{' '}
          <span className="font-bold">{subscription?.product.name ?? '-'}</span>
        </p>
        {user?.is_subscription_active ? (
          <p>
            {isSubscriptionCancelled ? t('subscriptionPage.activeUntil') : t('subscriptionPage.nextBillingDate')}:{' '}
            <span className="font-bold">
              {subscription?.current_period_end
                ? dayjs(subscription?.current_period_end).local().format('YYYY-MM-DD')
                : '-'}
            </span>
          </p>
        ) : null}
        {user?.is_subscription_active && !isSubscriptionCancelled ? (
          <LoadingButton
            disabled={isSuccessCancelSubscription}
            loading={isLoadingCancelSubscription}
            color="error"
            variant="outline"
            className="w-[350px] max-w-full"
            onClick={handleCancelSubscription}
          >
            {t('button.cancel_subscription')}
          </LoadingButton>
        ) : null}
        {user?.is_subscription_active && isSubscriptionCancelled ? (
          <LoadingButton
            disabled={isSuccessReactivateSubscription}
            loading={isLoadingReactivateSubscription}
            color="primary"
            className="w-[350px] max-w-full"
            onClick={handleReactivateSubscription}
          >
            {t('button.restore_subscription')}
          </LoadingButton>
        ) : null}
        {!user?.is_subscription_active ? (
          <Button type="button" color="primary" className="w-[350px] max-w-full" onClick={handleGoToPaymentPage}>
            {t('button.upgrade')}
          </Button>
        ) : null}
      </div>
    </PageLayout>
  );
};

export default SubscriptionPage;
